/* eslint-disable max-len */
import { ActionsPopover, Button } from "@dit/core-frontend"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"

import { Box, Stack, Text } from "@mos-cat/ds"
import { Add } from "@mos-cat/ds-icons"
import {
  $createParagraphNode,
  $getRoot,
  $getSelection,
  $isElementNode,
  $isParagraphNode,
  $isRangeSelection,
  $isTextNode,
} from "lexical"
import * as React from "react"
import { useCallback, useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import ColorPickerModal from "../components/ColorPicker/ColorPickerModal"
import { ColumnIcon } from "../theme/editorIcons"
import { $isTableSelection } from "./TableCellResizer"

import { invariant } from "../utils/invariant"
import {
  $deleteTableColumn__EXPERIMENTAL,
  $deleteTableRow__EXPERIMENTAL,
  $getNodeTriplet,
  $getTableCellNodeFromLexicalNode,
  $getTableNodeFromLexicalNodeOrThrow,
  $insertTableColumn__EXPERIMENTAL,
  $insertTableRow__EXPERIMENTAL,
  $unmergeCell,
} from "../nodes/TableNode/LexicalTebleUtils"
import { $isTableCellNode, $isTableRowNode, TableCellNode } from "../nodes/TableNode"
import { getTableObserverFromTableElement } from "../nodes/TableNode/lexicalSelectionHelpers"

function computeSelectionCount(selection) {
  const selectionShape = selection?.getShape()
  return {
    columns: selectionShape.toX - selectionShape.fromX + 1,
    rows: selectionShape.toY - selectionShape.fromY + 1,
  }
}

// This is important when merging cells as there is no good way to re-merge weird shapes (a result
// of selecting merged cells and non-merged)
function isTableSelectionRectangular(selection) {
  const nodes = selection.getNodes()
  const currentRows = []
  let currentRow = null
  let expectedColumns = null
  let currentColumns = 0
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i]
    if ($isTableCellNode(node)) {
      const row = node.getParentOrThrow()
      invariant($isTableRowNode(row), "Expected CellNode to have a RowNode parent")
      if (currentRow !== row) {
        if (expectedColumns !== null && currentColumns !== expectedColumns) {
          return false
        }
        if (currentRow !== null) {
          expectedColumns = currentColumns
        }
        currentRow = row
        currentColumns = 0
      }
      const colSpan = node.__colSpan
      for (let j = 0; j < colSpan; j++) {
        if (currentRows[currentColumns + j] === undefined) {
          currentRows[currentColumns + j] = 0
        }
        currentRows[currentColumns + j] += node.__rowSpan
      }
      currentColumns += colSpan
    }
  }
  return (
    (expectedColumns === null || currentColumns === expectedColumns) &&
    currentRows.every((v) => v === currentRows[0])
  )
}

function $canUnmerge() {
  const selection = $getSelection()
  if (
    ($isRangeSelection(selection) && !selection.isCollapsed()) ||
    ($isTableSelection(selection) && !selection.anchor.is(selection.focus)) ||
    (!$isRangeSelection(selection) && !$isTableSelection(selection))
  ) {
    return false
  }
  const [cell] = $getNodeTriplet(selection.anchor)
  return cell.__colSpan > 1 || cell.__rowSpan > 1
}

function $cellContainsEmptyParagraph(cell) {
  if (cell.getChildrenSize() !== 1) {
    return false
  }
  const firstChild = cell.getFirstChildOrThrow()
  if (!$isParagraphNode(firstChild) || !firstChild.isEmpty()) {
    return false
  }
  return true
}

function $selectLastDescendant(node) {
  const lastDescendant = node.getLastDescendant()
  if ($isTextNode(lastDescendant)) {
    lastDescendant.select()
  } else if ($isElementNode(lastDescendant)) {
    lastDescendant.selectEnd()
  } else if (lastDescendant !== null) {
    lastDescendant.selectNext()
  }
}

function currentCellBackgroundColor(editor) {
  return editor.getEditorState().read(() => {
    const selection = $getSelection()
    if ($isRangeSelection(selection) || $isTableSelection(selection)) {
      const [cell] = $getNodeTriplet(selection.anchor)
      if ($isTableCellNode(cell)) {
        return cell.getBackgroundColor()
      }
    }
    return null
  })
}

function TableActionMenu({
  onClose,
  tableCellNode: _tableCellNode,
  setIsMenuOpen,
  contextRef,
  menuRootRef,
}) {
  const [editor] = useLexicalComposerContext()
  const dropDownRef = useRef(null)
  const [tableCellNode, updateTableCellNode] = useState(_tableCellNode)
  const [selectionCounts, updateSelectionCounts] = useState({
    columns: 1,
    rows: 1,
  })
  const [canMergeCells, setCanMergeCells] = useState(false)
  const [canUnmergeCell, setCanUnmergeCell] = useState(false)
  const [backgroundColor, setBackgroundColor] = useState(
    () => currentCellBackgroundColor(editor) || "",
  )
  const [showModal, setShowModal] = useState(null)
  const handleSetModal = (value) => {
    setShowModal(value)
  }

  useEffect(() => {
    return editor.registerMutationListener(
      TableCellNode,
      (nodeMutations) => {
        const nodeUpdated = nodeMutations.get(tableCellNode.getKey()) === "updated"

        if (nodeUpdated) {
          editor.getEditorState().read(() => {
            updateTableCellNode(tableCellNode.getLatest())
          })
          setBackgroundColor(currentCellBackgroundColor(editor) || "")
        }
      },
      { skipInitialization: true },
    )
  }, [editor, tableCellNode])

  // useEffect(() => {
  //   editor.getEditorState().read(() => {
  //     const selection = $getSelection()
  //     // Merge cells
  //     if ($isTableSelection(selection)) {
  //       const currentSelectionCounts = computeSelectionCount(selection)
  //       updateSelectionCounts(computeSelectionCount(selection))
  //       setCanMergeCells(
  //         isTableSelectionRectangular(selection) &&
  //           (currentSelectionCounts.columns > 1 || currentSelectionCounts.rows > 1),
  //       )
  //     }
  //     // Unmerge cell
  //     setCanUnmergeCell($canUnmerge())
  //   })
  // }, [editor])

  useEffect(() => {
    const menuButtonElement = contextRef.current
    const dropDownElement = dropDownRef.current
    const rootElement = editor.getRootElement()

    if (menuButtonElement != null && dropDownElement != null && rootElement != null) {
      const rootEleRect = rootElement.getBoundingClientRect()
      const menuButtonRect = menuButtonElement.getBoundingClientRect()
      dropDownElement.style.opacity = "1"
      const dropDownElementRect = dropDownElement.getBoundingClientRect()
      const margin = 5
      let leftPosition = menuButtonRect.right + margin
      if (
        leftPosition + dropDownElementRect.width > window.innerWidth ||
        leftPosition + dropDownElementRect.width > rootEleRect.right
      ) {
        const position = menuButtonRect.left - dropDownElementRect.width - margin
        leftPosition = (position < 0 ? margin : position) + window.pageXOffset
      }
      dropDownElement.style.left = `${leftPosition + window.pageXOffset}px`

      let topPosition = menuButtonRect.top
      if (topPosition + dropDownElementRect.height > window.innerHeight) {
        const position = menuButtonRect.bottom - dropDownElementRect.height
        topPosition = (position < 0 ? margin : position) + window.pageYOffset
      }
      dropDownElement.style.top = `${topPosition + +window.pageYOffset}px`
    }
  }, [contextRef, dropDownRef, editor])

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        dropDownRef.current != null &&
        contextRef.current != null &&
        !dropDownRef.current.contains(event.target) &&
        !contextRef.current.contains(event.target)
      ) {
        setIsMenuOpen(false)
      }
    }

    window.addEventListener("click", handleClickOutside)

    return () => window.removeEventListener("click", handleClickOutside)
  }, [setIsMenuOpen, contextRef])

  const clearTableSelection = useCallback(() => {
    editor.update(() => {
      if (tableCellNode.isAttached()) {
        const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode)
        const tableElement = editor.getElementByKey(tableNode.getKey())

        if (!tableElement) {
          throw new Error("Expected to find tableElement in DOM")
        }

        const tableSelection = getTableObserverFromTableElement(tableElement)
        if (tableSelection !== null) {
          tableSelection.clearHighlight()
        }

        tableNode.markDirty()
        updateTableCellNode(tableCellNode.getLatest())
      }

      const rootNode = $getRoot()
      rootNode.selectStart()
    })
  }, [editor, tableCellNode])

  const mergeTableCellsAtSelection = () => {
    editor.update(() => {
      const selection = $getSelection()
      if ($isTableSelection(selection)) {
        const { columns, rows } = computeSelectionCount(selection)
        const nodes = selection.getNodes()
        let firstCell = null
        for (let i = 0; i < nodes.length; i++) {
          const node = nodes[i]
          if ($isTableCellNode(node)) {
            if (firstCell === null) {
              node.setColSpan(columns).setRowSpan(rows)
              firstCell = node
              const isEmpty = $cellContainsEmptyParagraph(node)
              let firstChild
              if (isEmpty && $isParagraphNode((firstChild = node.getFirstChild()))) {
                firstChild.remove()
              }
            } else if ($isTableCellNode(firstCell)) {
              const isEmpty = $cellContainsEmptyParagraph(node)
              if (!isEmpty) {
                firstCell.append(...node.getChildren())
              }
              node.remove()
            }
          }
        }
        if (firstCell !== null) {
          if (firstCell.getChildrenSize() === 0) {
            firstCell.append($createParagraphNode())
          }
          $selectLastDescendant(firstCell)
        }
        onClose()
      }
    })
  }

  const unmergeTableCellsAtSelection = () => {
    editor.update(() => {
      $unmergeCell()
    })
  }

  const insertTableRowAtSelection = useCallback(
    (shouldInsertAfter) => {
      editor.update(() => {
        $insertTableRow__EXPERIMENTAL(shouldInsertAfter)
        onClose()
      })
    },
    [editor, onClose],
  )

  const insertTableColumnAtSelection = useCallback(
    (shouldInsertAfter) => {
      editor.update(() => {
        for (let i = 0; i < selectionCounts.columns; i++) {
          $insertTableColumn__EXPERIMENTAL(shouldInsertAfter)
        }
        onClose()
      })
    },
    [editor, onClose, selectionCounts.columns],
  )

  const deleteTableRowAtSelection = useCallback(() => {
    editor.update(() => {
      $deleteTableRow__EXPERIMENTAL()
      onClose()
    })
  }, [editor, onClose])

  const deleteTableAtSelection = useCallback(() => {
    editor.update(() => {
      const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode)
      tableNode.remove()

      clearTableSelection()
      onClose()
    })
  }, [editor, tableCellNode, clearTableSelection, onClose])

  const deleteTableColumnAtSelection = useCallback(() => {
    editor.update(() => {
      $deleteTableColumn__EXPERIMENTAL()
      onClose()
    })
  }, [editor, onClose])

  const handleCellBackgroundColor = useCallback(
    (value) => {
      editor.update(() => {
        const selection = $getSelection()
        if ($isRangeSelection(selection) || $isTableSelection(selection)) {
          const [cell] = $getNodeTriplet(selection.anchor)
          if ($isTableCellNode(cell)) {
            cell.setBackgroundColor(value)
          }

          if ($isTableSelection(selection)) {
            const nodes = selection.getNodes()

            for (let i = 0; i < nodes.length; i++) {
              const node = nodes[i]
              if ($isTableCellNode(node)) {
                node.setBackgroundColor(value)
              }
            }
          }
        }
      })
    },
    [editor],
  )

  return (
    <>
      <ActionsPopover
        portalContainer={document.body}
        customHoverStyle={{ backgroundColor: "none", boxShadow: "none" }}
        popoverProps={{ minWidth: "250px" }}
        content={
          <button
            style={{
              backgroundColor: "white",
              zIndex: "10",
              borderRadius: "5px",
              marginTop: "-2px",
              outline: "none",
            }}
            // onClick={(e) => {
            //   e.stopPropagation()
            //   setIsMenuOpen(!isMenuOpen)
            // }}
            ref={menuRootRef}
          >
            {" "}
            <Add size="10px" />
          </button>
        }
      >
        {(buttonProps) => (
          <>
            <Button
              {...buttonProps}
              kind="borderless"
              onClick={() => handleSetModal("change-color")}
            >
              <ColumnIcon />
              <Text>Цвет ячейки</Text>
            </Button>

            <Button
              {...buttonProps}
              kind="borderless"
              className="item"
              onClick={() => insertTableRowAtSelection(false)}
            >
              <>
                Добавить {selectionCounts.rows === 1 ? "строку" : `${selectionCounts.rows} строки`}{" "}
                выше
              </>
            </Button>
            <Button
              {...buttonProps}
              kind="borderless"
              className="item"
              onClick={() => insertTableRowAtSelection(true)}
            >
              <>
                Добавить {selectionCounts.rows === 1 ? "строку" : `${selectionCounts.rows} строки`}{" "}
                ниже
              </>
            </Button>

            <Button
              {...buttonProps}
              kind="borderless"
              className="item"
              onClick={() => insertTableColumnAtSelection(false)}
            >
              <>
                Добавить{" "}
                {selectionCounts.columns === 1 ? "столбец" : `${selectionCounts.columns} столбца`}{" "}
                слева
              </>
            </Button>
            <Button
              {...buttonProps}
              kind="borderless"
              className="item"
              onClick={() => insertTableColumnAtSelection(true)}
            >
              <>
                Добавить{" "}
                {selectionCounts.columns === 1 ? "столбец" : `${selectionCounts.columns} столбца`}{" "}
                справа
              </>
            </Button>
            <hr />
            <Button
              {...buttonProps}
              kind="borderless"
              className="item"
              onClick={() => deleteTableColumnAtSelection()}
            >
              <>Удалить столбец</>
            </Button>
            <Button
              {...buttonProps}
              kind="borderless"
              className="item"
              onClick={() => deleteTableRowAtSelection()}
            >
              <>Удалить строку</>
            </Button>
            <Button
              {...buttonProps}
              kind="borderless"
              className="item"
              onClick={() => deleteTableAtSelection()}
            >
              <>Удалить таблицу</>
            </Button>
          </>
        )}
      </ActionsPopover>

      <ColorPickerModal
        color={backgroundColor}
        onChangeColor={handleCellBackgroundColor}
        modalDisclosureProps={{
          as: Stack,
          position: "relative",
          styles: { cursor: "pointer" },
        }}
        modalStateProps={{
          visible: showModal === "change-color",
          hide: () => handleSetModal(null),
        }}
      />
    </>
  )
}

function TableCellActionMenuContainer({ anchorElem, cellMerge }) {
  const [editor] = useLexicalComposerContext()
  const menuButtonRef = useRef(null)
  const menuRootRef = useRef(null)
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const [tableCellNode, setTableMenuCellNode] = useState(null)

  const moveMenu = useCallback(() => {
    const menu = menuButtonRef.current
    const selection = $getSelection()
    const nativeSelection = window.getSelection()
    const activeElement = document.activeElement

    if (selection == null || menu == null) {
      setTableMenuCellNode(null)
      return
    }

    const rootElement = editor.getRootElement()

    if (
      $isRangeSelection(selection) &&
      rootElement !== null &&
      nativeSelection !== null &&
      rootElement.contains(nativeSelection.anchorNode)
    ) {
      const tableCellNodeFromSelection = $getTableCellNodeFromLexicalNode(
        selection.anchor.getNode(),
      )

      if (tableCellNodeFromSelection == null) {
        setTableMenuCellNode(null)
        return
      }

      const tableCellParentNodeDOM = editor.getElementByKey(tableCellNodeFromSelection.getKey())

      if (tableCellParentNodeDOM == null) {
        setTableMenuCellNode(null)
        return
      }

      setTableMenuCellNode(tableCellNodeFromSelection)
    } else if (!activeElement) {
      setTableMenuCellNode(null)
    }
  }, [editor])

  useEffect(() => {
    return editor.registerUpdateListener(() => {
      editor.getEditorState().read(() => {
        moveMenu()
      })
    })
  })

  useEffect(() => {
    const menuButtonDOM = menuButtonRef.current

    if (menuButtonDOM != null && tableCellNode != null) {
      const tableCellNodeDOM = editor.getElementByKey(tableCellNode.getKey())

      if (tableCellNodeDOM != null) {
        const tableCellRect = tableCellNodeDOM.getBoundingClientRect()
        const menuRect = menuButtonDOM.getBoundingClientRect()
        const anchorRect = anchorElem.getBoundingClientRect()

        menuButtonDOM.style.opacity = "1"

        menuButtonDOM.style.left = `${
          tableCellRect.right - menuRect.width - 10 - anchorRect.left
        }px`

        menuButtonDOM.style.top = `${tableCellRect.top - anchorRect.top + 4}px`
      } else {
        menuButtonDOM.style.opacity = "0"
      }
    }
  }, [menuButtonRef, tableCellNode, editor, anchorElem])

  const prevTableCellDOM = useRef(tableCellNode)

  useEffect(() => {
    if (prevTableCellDOM.current !== tableCellNode) {
      setIsMenuOpen(false)
    }

    prevTableCellDOM.current = tableCellNode
  }, [prevTableCellDOM, tableCellNode])

  return (
    <div
      style={{ position: "absolute", top: 0, left: 0, willChange: "transform", zIndex: 10 }}
      ref={menuButtonRef}
    >
      {tableCellNode != null && (
        <>
          <Box
            position="relative"
            display="flex"
            alignItems="center"

            // className="table-cell-action-button chevron-down"
          >
            <TableActionMenu
              menuRootRef={menuRootRef}
              contextRef={menuRootRef}
              setIsMenuOpen={setIsMenuOpen}
              onClose={() => setIsMenuOpen(false)}
              tableCellNode={tableCellNode}
            />
          </Box>
        </>
      )}
    </div>
  )
}

export default function TableActionMenuPlugin({ anchorElem = document.body, cellMerge = false }) {
  return createPortal(
    <TableCellActionMenuContainer anchorElem={anchorElem} cellMerge={cellMerge} />,
    anchorElem,
  )
}
